import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AdminBlogPostTool from "./AdminBlogPostTool";
import "../../../App.css";

const AnalyticsStatCard = ({ label, value }) => (
  <div className="profile-stat-cell analytics-dossier-stat">
    <div className="profile-stat-label">{label}</div>
    <div className="profile-stat-value analytics-dossier-stat-value">{value}</div>
  </div>
);

const Analytics = () => {
  const [messageCycles, setMessageCycles] = useState([]);
  const [userMap, setUserMap] = useState({});
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadAnalytics = async () => {
      try {
        const [messageCountsRes, analyticsRes] = await Promise.all([
          fetch("/api/message-counts", {
            credentials: "include",
            cache: "no-store",
          }),
          fetch("/api/analytics", {
            credentials: "include",
            cache: "no-store",
          }),
        ]);

        const messageCountsData = await messageCountsRes.json();
        const analyticsPayload = await analyticsRes.json();

        setMessageCycles(Array.isArray(messageCountsData) ? messageCountsData : []);
        setAnalyticsData(analyticsPayload || null);

        const allUserIds = Array.from(
          new Set(
            (Array.isArray(messageCountsData) ? messageCountsData : []).flatMap((cycle) =>
              Object.keys(cycle.users || {})
            )
          )
        );

        const resolvedUsers = await Promise.all(
          allUserIds.map((id) =>
            fetch(`/api/resolve-user/${id}`, {
              credentials: "include",
              cache: "no-store",
            })
              .then((res) => res.json())
              .then((user) => ({ [id]: user }))
              .catch(() => ({ [id]: null }))
          )
        );

        const mappedUsers = resolvedUsers.reduce(
          (acc, cur) => ({ ...acc, ...cur }),
          {}
        );

        setUserMap(mappedUsers);
      } catch (error) {
        console.error("❌ Failed to load analytics:", error);
      } finally {
        setLoading(false);
      }
    };

    loadAnalytics();
  }, []);

  const topRoutesText = useMemo(() => {
    if (!analyticsData?.topRoutes?.length) return "No data";

    return analyticsData.topRoutes
      .slice(0, 3)
      .map((entry) => `${entry.route} (${entry.views})`)
      .join("\n");
  }, [analyticsData]);

  return (
    <div className="profile-shell">
      <div className="profile-panel analytics-dossier-panel">
        <div className="profile-topbar">
          <div>
            <div className="profile-kicker">OFFICE DOSSIER</div>
            <h1 className="profile-title">Pogforts Analytics</h1>
            <h2 className="profile-subtitle">
              Intelligence tracking, behavioural signals, and cycle reconnaissance.
            </h2>
          </div>

          <div className="profile-top-actions">
            <AdminBlogPostTool />
            <Link to="/online/profile" className="profile-admin-button">
              RETURN TO PROFILE
            </Link>
          </div>
        </div>

        <div className="analytics-dossier-summary">
          <AnalyticsStatCard
            label="Page Views"
            value={loading ? "..." : analyticsData?.totalPageViews ?? 0}
          />
          <AnalyticsStatCard
            label="Unique Users"
            value={loading ? "..." : analyticsData?.uniqueUsers ?? 0}
          />
          <AnalyticsStatCard
            label="Right-Click Attempts"
            value={loading ? "..." : analyticsData?.signalCounts?.rightClickAttempts ?? 0}
          />
          <AnalyticsStatCard
            label="Top Routes"
            value={loading ? "..." : topRoutesText}
          />
        </div>

        <h2 className="analytics-dossier-heading">7-Day Message Cycles</h2>

        <div className="analytics-dossier-grid">
          {messageCycles.map((cycle, index) => (
            <div className="analytics-dossier-card" key={index}>
              <div className="analytics-dossier-card-header">
                <h3>{cycle.label}</h3>
                <p>Total Messages: {cycle.totalMessages}</p>
              </div>

              <div className="analytics-dossier-scroll">
                <div className="analytics-dossier-users-title">Users</div>

                <ul className="analytics-dossier-user-list">
                  {Object.entries(cycle.users || {})
                    .sort(([, a], [, b]) => b - a)
                    .map(([userId, count]) => {
                      const user = userMap[userId];

                      return (
                        <li key={userId} className="analytics-dossier-user-row">
                              <Link
                                to={`/analytics/user/${userId}`}
                                className="dossier-user-link"
                              >
                                <div className="analytics-dossier-user-left">
                            {user?.avatar ? (
                              <img
                                src={user.avatar}
                                alt=""
                                className="analytics-dossier-avatar"
                              />
                            ) : (
                              <div className="analytics-dossier-avatar analytics-dossier-avatar-fallback">
                                {String(userId).slice(-2)}
                              </div>
                            )}

                            <div className="analytics-dossier-user-meta">
                              {user ? (
                                <>
                                  <span className="analytics-dossier-username">
                                    {user.username}
                                    {user.discriminator ? `#${user.discriminator}` : ""}
                                  </span>
                                  <span className="analytics-dossier-userid">{userId}</span>
                                </>
                              ) : (
                                <code className="analytics-dossier-userid">
                                  {String(userId).slice(0, 6)}...{String(userId).slice(-4)}
                                </code>
                              )}
                            </div>
                          </div>

                          <div className="analytics-dossier-count">{count}</div>
                          </Link>
                        </li>
                      );
                    })}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Analytics;